/**
 * Upload Slip Dialog - Smart Slip Upload
 *
 * Dialog สำหรับอัปโหลดสลิปพร้อมการจับคู่สมาชิกอัตโนมัติ
 * Flow: Upload slip → Auto verify & match → Confirm → Submit
 */

import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Upload,
  X,
  Image as ImageIcon,
  User,
  Phone,
  CreditCard,
  Loader2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/components/auth/auth-provider'
import { SlipLogService } from '@/domain/slip-log'
import type { MatchedMember, SmartSlipUploadResult } from '@/domain/slip-log'
import { BankLogo } from '@/components/ui/bank-logo'
import { cn } from '@/lib/utils'

// ============================================
// Types
// ============================================

interface UploadSlipDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

type Step = 'upload' | 'matching' | 'confirm' | 'success'

// ============================================
// Component
// ============================================

export function UploadSlipDialog({
  open,
  onOpenChange,
  onSuccess
}: UploadSlipDialogProps) {
  const { sessionToken } = useAuth()

  // State - Step management
  const [step, setStep] = useState<Step>('upload')

  // State - Upload step
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  // State - Matching step
  const [isMatching, setIsMatching] = useState(false)
  const [matchResult, setMatchResult] = useState<SmartSlipUploadResult | null>(null)
  const [selectedMember, setSelectedMember] = useState<MatchedMember | null>(null)

  // State - Confirm step
  const [isSubmitting, setIsSubmitting] = useState(false)

  // State - Common
  const [error, setError] = useState<string | null>(null)

  // ============================================
  // File Upload
  // ============================================

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      // Validate size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('ไฟล์ใหญ่เกิน 5MB')
        return
      }

      // Validate type
      if (!file.type.startsWith('image/')) {
        setError('กรุณาเลือกไฟล์รูปภาพเท่านั้น')
        return
      }

      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      setError(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    maxFiles: 1,
  })

  const clearFile = () => {
    setSelectedFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  // ============================================
  // Step 1: Upload & Auto Verify
  // ============================================

  const handleVerifyAndMatch = async () => {
    if (!selectedFile || !sessionToken) {
      setError('กรุณาอัปโหลดรูปสลิป')
      return
    }

    setIsMatching(true)
    setError(null)

    try {
      // Call smart upload API - it will verify slip and match members automatically
      const result = await SlipLogService.uploadSlipSmart(selectedFile, sessionToken)

      console.log('✅ Match result:', result)

      // Check if slip verification failed
      if (!result.slip_data.is_valid) {
        setError('ตรวจสอบสลิปไม่สำเร็จ กรุณาตรวจสอบรูปสลิป')
        return
      }

      setMatchResult(result)

      // Auto-select if only 1 candidate with high confidence
      if (result.match_result.auto_select && result.match_result.candidates.length === 1) {
        setSelectedMember(result.match_result.candidates[0])
      }

      setStep('matching')
    } catch (error: any) {
      console.error('❌ Match error:', error)
      setError(error.message || 'เกิดข้อผิดพลาดในการตรวจสอบสลิป')
    } finally {
      setIsMatching(false)
    }
  }

  // ============================================
  // Step 2: Select Member
  // ============================================

  const handleMemberSelect = (member: MatchedMember) => {
    setSelectedMember(member)
  }

  const handleConfirmMatch = () => {
    if (!selectedMember) {
      setError('กรุณาเลือกสมาชิก')
      return
    }
    setStep('confirm')
  }

  const handleBackToMatching = () => {
    setStep('matching')
  }

  // ============================================
  // Step 3: Final Confirm & Submit
  // ============================================

  const handleFinalSubmit = async () => {
    if (!selectedFile || !selectedMember || !sessionToken) {
      setError('ข้อมูลไม่ครบถ้วน')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Call original upload API with selected member
      const result = await SlipLogService.uploadSlip({
        slipImage: selectedFile,
        member_id: selectedMember.member_id,
        qr_text: 'panel',
      }, sessionToken)

      if (result.success) {
        setStep('success')
        setTimeout(() => {
          onSuccess?.()
          handleClose()
        }, 1500)
      } else {
        setError(result.message)
      }
    } catch (error: any) {
      console.error('❌ Submit error:', error)
      setError(error.message || 'เกิดข้อผิดพลาดในการอัปโหลด')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ============================================
  // Close Dialog
  // ============================================

  const handleClose = () => {
    clearFile()
    setMatchResult(null)
    setSelectedMember(null)
    setError(null)
    setStep('upload')
    onOpenChange(false)
  }

  // ============================================
  // Render Steps
  // ============================================

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            อัปโหลดสลิป (Smart Upload)
          </DialogTitle>
          <DialogDescription>
            อัปโหลดรูปสลิป ระบบจะตรวจสอบและจับคู่สมาชิกอัตโนมัติ
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 py-4">
          <div className={cn(
            "flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold",
            step === 'upload' ? "bg-blue-600 text-white" :
            ['matching', 'confirm', 'success'].includes(step) ? "bg-emerald-500 text-white" :
            "bg-zinc-200 dark:bg-zinc-700 text-zinc-500"
          )}>
            1
          </div>
          <div className={cn(
            "h-0.5 w-12",
            ['matching', 'confirm', 'success'].includes(step) ? "bg-emerald-500" : "bg-zinc-200 dark:bg-zinc-700"
          )} />
          <div className={cn(
            "flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold",
            step === 'matching' ? "bg-blue-600 text-white" :
            ['confirm', 'success'].includes(step) ? "bg-emerald-500 text-white" :
            "bg-zinc-200 dark:bg-zinc-700 text-zinc-500"
          )}>
            2
          </div>
          <div className={cn(
            "h-0.5 w-12",
            ['confirm', 'success'].includes(step) ? "bg-emerald-500" : "bg-zinc-200 dark:bg-zinc-700"
          )} />
          <div className={cn(
            "flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold",
            step === 'confirm' ? "bg-blue-600 text-white" :
            step === 'success' ? "bg-emerald-500 text-white" :
            "bg-zinc-200 dark:bg-zinc-700 text-zinc-500"
          )}>
            3
          </div>
        </div>

        {/* Success State */}
        {step === 'success' && (
          <div className="py-12 flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-lg">อัปโหลดสำเร็จ!</p>
              <p className="text-muted-foreground text-sm mt-1">
                ระบบกำลังตรวจสอบสลิป...
              </p>
            </div>
          </div>
        )}

        {/* Step 1: Upload Slip */}
        {step === 'upload' && (
          <div className="space-y-6 py-4">
            {/* File Upload */}
            <div className="space-y-2">
              {previewUrl ? (
                <div className="relative rounded-xl overflow-hidden border-2 border-dashed border-emerald-500/50 bg-emerald-500/5">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full max-h-[400px] object-contain"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 h-8 w-8"
                    onClick={clearFile}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/50 text-white text-sm">
                    {selectedFile?.name} ({((selectedFile?.size || 0) / 1024 / 1024).toFixed(2)} MB)
                  </div>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={cn(
                    "border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all",
                    isDragActive
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-zinc-300 dark:border-zinc-700 hover:border-blue-500/50 hover:bg-blue-500/5"
                  )}
                >
                  <input {...getInputProps()} />
                  <div className="flex flex-col items-center gap-4">
                    <div className={cn(
                      "w-16 h-16 rounded-full flex items-center justify-center",
                      isDragActive ? "bg-blue-500/20" : "bg-zinc-100 dark:bg-zinc-800"
                    )}>
                      <ImageIcon className={cn(
                        "w-8 h-8",
                        isDragActive ? "text-blue-500" : "text-muted-foreground"
                      )} />
                    </div>
                    <div>
                      <p className="font-medium text-lg">
                        {isDragActive ? 'วางไฟล์ที่นี่' : 'ลากไฟล์มาวาง หรือคลิกเพื่อเลือก'}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        รองรับ PNG, JPG, GIF, WEBP (สูงสุด 5MB)
                      </p>
                      <p className="text-xs text-muted-foreground mt-3">
                        💡 ระบบจะอ่านข้อมูลจากสลิปอัตโนมัติและจับคู่สมาชิก
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg flex items-center gap-2 text-rose-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isMatching}
              >
                ยกเลิก
              </Button>
              <Button
                onClick={handleVerifyAndMatch}
                disabled={!selectedFile || isMatching}
                className="gap-2 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700"
              >
                {isMatching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    กำลังตรวจสอบ...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    ตรวจสอบสลิป
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Matching Results */}
        {step === 'matching' && matchResult && (
          <div className="space-y-6 py-4">
            {/* Slip Info */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">
                ข้อมูลจากสลิป
              </p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">จำนวนเงิน</p>
                  <p className="font-semibold">{matchResult.slip_data.amount || '-'} บาท</p>
                </div>
                <div>
                  <p className="text-muted-foreground">เลขบัญชีผู้ส่ง</p>
                  <p className="font-mono text-xs">{matchResult.slip_data.sender_account || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">ชื่อผู้ส่ง</p>
                  <p>{matchResult.slip_data.sender_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">วันเวลา</p>
                  <p className="text-xs">
                    {matchResult.slip_data.datetime ? new Date(matchResult.slip_data.datetime).toLocaleString('th-TH') : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Duplicate Warnings */}
            {matchResult.duplicate_result.has_duplicates && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-amber-900 dark:text-amber-100">
                      พบรายการซ้ำ
                    </p>
                    <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                      พบรายการที่อาจซ้ำกันใน {matchResult.duplicate_result.duplicates.length} แหล่ง
                    </p>
                    <div className="mt-3 space-y-2">
                      {matchResult.duplicate_result.duplicates.map((dup, idx) => (
                        <div key={idx} className="text-sm bg-white/50 dark:bg-zinc-900/50 p-2 rounded">
                          <span className="font-medium">{dup.source}</span>: {dup.count} รายการ
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Matched Members */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <User className="w-4 h-4" />
                ผลการจับคู่สมาชิก
                {matchResult.match_result.found && (
                  <span className="text-sm font-normal text-muted-foreground">
                    (พบ {matchResult.match_result.candidates.length} คน)
                  </span>
                )}
              </h3>

              {!matchResult.match_result.found ? (
                <div className="p-8 text-center bg-zinc-50 dark:bg-zinc-900 rounded-lg">
                  <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium">ไม่พบสมาชิกที่ตรงกัน</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {matchResult.match_result.reason || 'ไม่พบสมาชิกที่มีเลขบัญชีนี้'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {matchResult.match_result.candidates.map((member) => (
                    <button
                      key={member.member_id}
                      type="button"
                      onClick={() => handleMemberSelect(member)}
                      className={cn(
                        "w-full p-4 text-left rounded-lg border-2 transition-all",
                        selectedMember?.member_id === member.member_id
                          ? "border-emerald-500 bg-emerald-500/10"
                          : "border-zinc-200 dark:border-zinc-700 hover:border-blue-500/50 hover:bg-blue-500/5"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white font-semibold text-lg">
                          {member.name?.[0] || member.agent_username?.[0] || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold">{member.name}</span>
                            <span className="text-xs text-muted-foreground">
                              @{member.agent_username}
                            </span>
                            <span className={cn(
                              "ml-auto px-2 py-0.5 rounded-full text-xs font-semibold",
                              member.confidence >= 95 ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300" :
                              member.confidence >= 90 ? "bg-blue-500/20 text-blue-700 dark:text-blue-300" :
                              "bg-amber-500/20 text-amber-700 dark:text-amber-300"
                            )}>
                              {member.confidence}% แม่นยำ
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {member.mobile_phone || member.phone}
                            </span>
                            <span className="flex items-center gap-1">
                              <CreditCard className="w-3 h-3" />
                              {member.bank_account}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            {member.match_reason}
                          </p>
                          {member.context.has_pending_deposit && (
                            <div className="mt-2 text-xs bg-blue-500/10 text-blue-700 dark:text-blue-300 p-2 rounded">
                              💡 มีรายการฝากค้าง {member.context.pending_amount} บาท
                            </div>
                          )}
                        </div>
                        <BankLogo bankValue={member.bank_name} size="sm" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg flex items-center gap-2 text-rose-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('upload')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                กลับ
              </Button>
              <Button
                onClick={handleConfirmMatch}
                disabled={!selectedMember}
                className="gap-2 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700"
              >
                ถัดไป
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && selectedMember && matchResult && (
          <div className="space-y-6 py-4">
            <div className="text-center">
              <h3 className="font-semibold text-lg mb-2">ยืนยันการอัปโหลดสลิป</h3>
              <p className="text-sm text-muted-foreground">
                กรุณาตรวจสอบข้อมูลก่อนยืนยัน
              </p>
            </div>

            {/* Selected Member */}
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mb-3">
                สมาชิกที่เลือก
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-semibold text-lg">
                  {selectedMember.name?.[0] || '?'}
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{selectedMember.name}</p>
                  <p className="text-sm text-muted-foreground">
                    @{selectedMember.agent_username} • {selectedMember.mobile_phone || selectedMember.phone}
                  </p>
                </div>
                <BankLogo bankValue={selectedMember.bank_name} size="sm" />
              </div>
            </div>

            {/* Slip Info */}
            <div className="p-4 bg-zinc-50 dark:bg-zinc-900 rounded-lg space-y-2">
              <p className="text-sm font-semibold mb-3">ข้อมูลสลิป</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">จำนวนเงิน</p>
                  <p className="font-semibold">{matchResult.slip_data.amount || '-'} บาท</p>
                </div>
                <div>
                  <p className="text-muted-foreground">เลขบัญชีผู้ส่ง</p>
                  <p className="font-mono text-xs">{matchResult.slip_data.sender_account || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">ชื่อผู้ส่ง</p>
                  <p>{matchResult.slip_data.sender_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">วันเวลา</p>
                  <p className="text-xs">
                    {matchResult.slip_data.datetime ? new Date(matchResult.slip_data.datetime).toLocaleString('th-TH') : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Duplicate Warning */}
            {matchResult.duplicate_result.has_duplicates && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  <p className="font-semibold">คำเตือน: พบรายการซ้ำ</p>
                  <p className="mt-1">กรุณาตรวจสอบว่าไม่ใช่รายการซ้ำก่อนยืนยัน</p>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg flex items-center gap-2 text-rose-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleBackToMatching}
                disabled={isSubmitting}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                กลับ
              </Button>
              <Button
                onClick={handleFinalSubmit}
                disabled={isSubmitting}
                className="gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    กำลังอัปโหลด...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    ยืนยันและอัปโหลด
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
